import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
  Drawer,
  ToggleButtonGroup,
  ToggleButton,
  alpha,
} from '@mui/material';
import {
  Description as PageIcon,
  Tab as TabIcon,
  TableRows as SectionIcon,
  WebAsset as ModalIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { IDocDocumentation, IDocNode, IDocType } from '../../common/i-doc/i-doc.documentation';

const TypeIcon: React.FC<{ type: IDocType; active?: boolean }> = ({ type, active }) => {
  const sx = { fontSize: 18, color: active ? 'primary.main' : 'text.secondary' };
  switch (type) {
    case 'page': return <PageIcon sx={sx} />;
    case 'tab': return <TabIcon sx={sx} />;
    case 'section': return <SectionIcon sx={sx} />;
    case 'modal': return <ModalIcon sx={sx} />;
    default: return <PageIcon sx={sx} />;
  }
};

const TreeNode: React.FC<{
  node: IDocNode;
  depth: number;
  selectedNode: IDocNode | null;
  onNodeClick: (node: IDocNode) => void;
  onClose?: () => void;
}> = ({ node, depth, selectedNode, onNodeClick, onClose }) => {
  const { t } = useTranslation();
  const isSelected = selectedNode?.id === node.id;
  // Only the top-level node (depth 0) carries an i18n route key as label —
  // everything nested (tabs, modals, nested pages) is already a plain string.
  const label = depth === 0 ? t(node.label) : node.label;

  return (
    <>
      <ListItemButton
        selected={isSelected}
        onClick={() => {
          onNodeClick(node);
          if (onClose) onClose();
        }}
        title={node.id}
        sx={{
          pl: 2 + depth * 1.5,
          minHeight: depth === 0 ? 40 : 36,
          borderRadius: 0,
          '&.Mui-selected': {
            bgcolor: alpha('#1976d2', 0.08),
            color: 'primary.main',
            borderRight: '3px solid',
            borderColor: 'primary.main',
            '& .MuiListItemIcon-root': { color: 'primary.main' },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 0, mr: depth === 0 ? 1.5 : 1.2, color: isSelected ? 'primary.main' : depth === 0 ? 'text.secondary' : 'text.disabled' }}>
          <TypeIcon type={node.type} active={isSelected} />
        </ListItemIcon>
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontSize: depth === 0 ? '0.85rem' : '0.8rem',
            fontWeight: isSelected ? 600 : 400,
            noWrap: true,
          }}
        />
      </ListItemButton>

      {node.children?.map(child => (
        <TreeNode
          key={child.id}
          node={child}
          depth={depth + 1}
          selectedNode={selectedNode}
          onNodeClick={onNodeClick}
          onClose={onClose}
        />
      ))}
    </>
  );
};

interface DocumentationSidebarProps {
  tree: IDocDocumentation;
  selectedNode: IDocNode | null;
  onNodeClick: (node: IDocNode) => void;
  role: string;
  userRoles: string[];
  onRoleChange: (event: React.MouseEvent<HTMLElement>, newRole: string | null) => void;
  isMobileOpen?: boolean;
  onClose?: () => void;
  onExport?: () => void;
  exporting?: boolean;
}

export const DocumentationSidebar: React.FC<DocumentationSidebarProps> = ({
  tree,
  selectedNode,
  onNodeClick,
  role,
  userRoles,
  onRoleChange,
  isMobileOpen = false,
  onClose,
  onExport,
  exporting = false,
}) => {
  const { t } = useTranslation(['common', 'routes']);

  const sidebarContent = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        overflowY: 'auto',
      }}
    >
      {/* Header with Role Switcher */}
      <Box sx={{ px: 2, pt: 2, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography 
          variant="body2" 
          fontWeight={700} 
          align="center"
          sx={{ fontSize: '0.85rem', color: 'text.primary', textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {t('routes.documentation')}
        </Typography>
        
        {userRoles.length > 1 && (
            <ToggleButtonGroup
                value={role}
                exclusive
                onChange={(e, v) => {
                    if (v) {
                        onRoleChange(e, v);
                        if (onClose) onClose();
                    }
                }}
                size="small"
                color="primary"
                fullWidth
                sx={{ 
                    height: 32,
                    '& .MuiToggleButton-root': {
                        fontSize: '0.75rem',
                        py: 0,
                        textTransform: 'none',
                        fontWeight: 600
                    }
                }}
            >
                {userRoles.includes('expert') && <ToggleButton value="expert">Експерт</ToggleButton>}
                {userRoles.includes('client') && <ToggleButton value="client">Клієнт</ToggleButton>}
                {userRoles.includes('admin') && <ToggleButton value="admin">Адмін</ToggleButton>}
                {userRoles.includes('pm') && <ToggleButton value="pm">Продакт</ToggleButton>}
            </ToggleButtonGroup>
        )}

        {onExport && (
            <Button
                size="small"
                variant="outlined"
                fullWidth
                disabled={exporting}
                onClick={onExport}
                startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
                sx={{ fontSize: '0.75rem', textTransform: 'none' }}
            >
                {exporting ? 'Формую файл…' : `Експортувати в Markdown (${role})`}
            </Button>
        )}
      </Box>

      <Divider />

      <List 
        disablePadding 
        sx={{ py: 0.5 }}
        subheader={
          <ListSubheader 
            component="div" 
            sx={{ 
                fontWeight: 700, 
                bgcolor: 'background.paper', 
                lineHeight: '36px', 
                fontSize: '0.7rem',
                color: 'text.disabled',
                textTransform: 'uppercase'
            }}
          >
            Зміст ({role})
          </ListSubheader>
        }
      >
        {tree.groups.map((group: any) => (
          <React.Fragment key={group.name}>
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                    fontWeight: 800, 
                    color: 'primary.main', 
                    fontSize: '0.65rem',
                    letterSpacing: 1
                }}
              >
                {t(`common:${group.name}`, { defaultValue: group.name }).toUpperCase()}
              </Typography>
            </Box>
            
            {group.nodes.map((node: IDocNode) => (
              <TreeNode
                key={node.id}
                node={node}
                depth={0}
                selectedNode={selectedNode}
                onNodeClick={onNodeClick}
                onClose={onClose}
              />
            ))}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={isMobileOpen}
        onClose={onClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: (theme) => theme.zIndex.drawer + 2,
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 200 },
        }}
      >
        {sidebarContent}
      </Drawer>

      <Box
        sx={{
          width: 200,
          position: 'fixed',
          top: '52px',
          right: 0,
          height: 'calc(100vh - 52px)',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          bgcolor: 'background.paper',
          borderLeft: '1px solid',
          borderColor: 'divider',
          overflowY: 'auto',
          zIndex: (theme) => theme.zIndex.appBar - 1,
        }}
      >
        {sidebarContent}
      </Box>
    </>
  );
};
